import {
  isDraftColumnId,
  ROOT_PARENT,
  type CascadeColumn,
  type CascadeRef,
  type CascadeStack,
  type OpenColumn,
  type OpenRequest,
  type OpenResult,
} from './types';

const sameRef = (a: { type: string; id: string }, b: { type: string; id: string }): boolean =>
  a.type === b.type && a.id === b.id;

// ---------------------------------------------------------------------------
// Fan-out tree engine (ADR-0027)
//
// The public state is a flat `CascadeColumn[]` — a depth-first pre-order
// flattening of the tree, each non-root column carrying the flat `parent` index
// of its parent plus the `sourceListKey` of the slot it occupies. All surgery
// happens on an intermediate pointer tree so index bookkeeping never leaks into
// the transition logic; `flatten()` re-assigns parent indices at the end.
// ---------------------------------------------------------------------------

interface TreeNode {
  type: string;
  id: string;
  tab?: string;
  sourceListKey?: string;
  children: TreeNode[];
}

/** Build the pointer tree from the flat array. Returns null for an empty tree. */
function build(flat: CascadeStack): { root: TreeNode | null; byIndex: (TreeNode | null)[] } {
  const byIndex: (TreeNode | null)[] = flat.map((c) => ({
    type: c.type,
    id: c.id,
    tab: c.tab,
    sourceListKey: c.sourceListKey,
    children: [],
  }));
  let root: TreeNode | null = null;
  flat.forEach((c, i) => {
    const node = byIndex[i]!;
    if (c.parent === undefined) {
      // First parentless column wins as the root; any stray extra is dropped by
      // never being attached (defensive against malformed input).
      root ??= node;
    } else {
      const parent = byIndex[c.parent];
      if (parent) parent.children.push(node);
    }
  });
  return { root, byIndex };
}

/** Flatten the pointer tree back to a depth-first array with parent indices. */
function flatten(root: TreeNode | null): CascadeColumn[] {
  const out: CascadeColumn[] = [];
  const visit = (node: TreeNode, parentIndex: number | undefined): void => {
    const index = out.length;
    const col: CascadeColumn = { type: node.type, id: node.id };
    if (node.tab !== undefined) col.tab = node.tab;
    if (parentIndex !== undefined) {
      col.parent = parentIndex;
      if (node.sourceListKey !== undefined) col.sourceListKey = node.sourceListKey;
    }
    out.push(col);
    for (const child of node.children) visit(child, index);
  };
  if (root) visit(root, undefined);
  return out;
}

const normalize = (flat: CascadeStack): CascadeColumn[] => flatten(build(flat).root);

/** True when a `{type,id}` is already open anywhere in the tree. */
export function isOpen(stack: CascadeStack, ref: CascadeRef): boolean {
  return stack.some((c) => sameRef(c, ref));
}

/**
 * The flat indices of the column at `index` and all of its descendants
 * (its subtree), in no particular order. Out-of-range → `[]`. Used by the
 * close-preview to dim exactly the columns a close/replace would discard
 * (ADR-0027 rule E).
 */
export function subtreeIndices(stack: CascadeStack, index: number): number[] {
  if (index < 0 || index >= stack.length) return [];
  const out = [index];
  // A parent always precedes its descendants (pre-order), so a single forward
  // pass collects the whole subtree: an index joins once its parent is already in.
  const seen = new Set(out);
  for (let i = index + 1; i < stack.length; i++) {
    if (stack[i].parent !== undefined && seen.has(stack[i].parent!)) {
      out.push(i);
      seen.add(i);
    }
  }
  return out;
}

/**
 * The flat indices of the STRICT descendants of the column at `index` — its
 * subtree with the node itself removed. Out-of-range → `[]`. Used by the
 * close-preview: replacing a slot's occupant swaps that column in place (a new
 * record in the same position — it does NOT close), so the ✕ marks only what is
 * discarded as collateral, i.e. everything drilled in beneath the occupant
 * (ADR-0027 rule E).
 */
export function descendantIndices(stack: CascadeStack, index: number): number[] {
  return subtreeIndices(stack, index).filter((i) => i !== index);
}

/**
 * The flat index of the column currently occupying the `(parentIndex,
 * sourceListKey)` slot, or `null` when the slot is empty. `ROOT_PARENT` resolves
 * to the root column (whose slot a re-root replaces, clearing the whole board).
 * This is the column a pick in that slot's source list would replace.
 */
export function slotChildIndex(
  stack: CascadeStack,
  parentIndex: number,
  sourceListKey?: string,
): number | null {
  if (parentIndex === ROOT_PARENT) return stack.length > 0 ? 0 : null;
  const i = stack.findIndex((c) => c.parent === parentIndex && c.sourceListKey === sourceListKey);
  return i === -1 ? null : i;
}

/**
 * Resolve opening `req.column` as a child of `req.parentIndex` in the
 * `req.sourceListKey` slot (or at the root when `parentIndex === ROOT_PARENT`).
 *
 * Resolution order (ADR-0027 §C):
 *  1. `{type,id}` already open anywhere → `retrigger` (no structural change).
 *  2. slot holds a different record → `replace` (swap child, drop its subtree,
 *     siblings kept).
 *  3. slot empty → `append`.
 */
export function open(stack: CascadeStack, req: OpenRequest): OpenResult {
  if (isOpen(stack, req.column)) {
    return { tree: normalize(stack), outcome: 'retrigger' };
  }

  if (req.parentIndex === ROOT_PARENT) {
    return reRoot(stack, req.column);
  }

  const { root, byIndex } = build(stack);
  const parent = byIndex[req.parentIndex];
  if (!root || !parent) {
    // Unknown parent (malformed request) — no-op.
    return { tree: flatten(root), outcome: 'retrigger' };
  }

  const child = makeNode(req.column, req.sourceListKey);
  const slotIdx = parent.children.findIndex((c) => c.sourceListKey === req.sourceListKey);
  if (slotIdx === -1) {
    parent.children.push(child);
    return { tree: flatten(root), outcome: 'append' };
  }
  // Replace-in-slot: swap the child (dropping its subtree), keep its position
  // among the siblings so the strip layout stays stable. Carry the outgoing
  // occupant's open tab onto the incoming record of the same type (unless the
  // caller asked for a specific tab), so navigating between records in a list
  // keeps the reader on the tab they were viewing.
  child.tab = carriedTab(parent.children[slotIdx], child);
  parent.children[slotIdx] = child;
  return { tree: flatten(root), outcome: 'replace' };
}

/**
 * Replace the root column (the page list's single child slot). A different
 * record clears the whole downstream tree; the same record re-triggers.
 */
export function reRoot(stack: CascadeStack, column: OpenColumn): OpenResult {
  if (isOpen(stack, column)) {
    return { tree: normalize(stack), outcome: 'retrigger' };
  }
  const root = makeNode(column, undefined);
  // Replacing an existing root of the same type keeps the open tab (mirrors the
  // replace-in-slot carry-over). An empty board or a different-typed root starts
  // on the registry default.
  root.tab = carriedTab(stack[0], root);
  return { tree: flatten(root), outcome: stack.length === 0 ? 'append' : 'replace' };
}

/**
 * Remove the column at `index` and its entire subtree; siblings and their
 * subtrees are untouched. Closing the root clears the board.
 */
export function closeSubtree(stack: CascadeStack, index: number): CascadeColumn[] {
  if (index < 0 || index >= stack.length) return normalize(stack);
  const { root, byIndex } = build(stack);
  const target = byIndex[index];
  if (!root || !target) return flatten(root);
  if (target === root) return [];
  detach(root, target);
  return flatten(root);
}

/**
 * The tab the incoming column should adopt when it replaces `prev` in a slot.
 * The caller's explicit tab always wins; otherwise the outgoing occupant's tab
 * carries over, but only for a same-type swap (a different type has its own tab
 * vocabulary and starts on its registry default).
 */
function carriedTab(
  prev: { type: string; tab?: string } | undefined,
  incoming: { type: string; tab?: string },
): string | undefined {
  if (incoming.tab !== undefined) return incoming.tab;
  if (prev && prev.type === incoming.type) return prev.tab;
  return undefined;
}

function makeNode(column: OpenColumn, sourceListKey: string | undefined): TreeNode {
  return {
    type: column.type,
    id: column.id,
    tab: column.tab,
    sourceListKey,
    children: [],
  };
}

/** Remove `target` from wherever it hangs in the tree rooted at `root`. */
function detach(root: TreeNode, target: TreeNode): void {
  const stack: TreeNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop()!;
    const idx = node.children.indexOf(target);
    if (idx !== -1) {
      node.children.splice(idx, 1);
      return;
    }
    stack.push(...node.children);
  }
}

/**
 * Swap the most-recently-added `:new` draft column's id for its real id after a
 * save. Structure and slot linkage are untouched. Naming the `type` swaps that type's
 * draft column, past a newer draft of another type opened beside it; naming the
 * `draftId` swaps exactly that column, past a newer draft of the same type.
 */
export function swapDraftId(
  stack: CascadeStack,
  realId: string,
  type?: string,
  draftId?: string,
): CascadeColumn[] {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (
      isDraftColumnId(stack[i].id) &&
      (type === undefined || stack[i].type === type) &&
      (draftId === undefined || stack[i].id === draftId)
    ) {
      const next = stack.slice();
      next[i] = { ...stack[i], id: realId };
      return next;
    }
  }
  return stack.slice();
}
